import type { PaginatedList } from "./common";
import type { CulturalActivity, SignatureRegime } from "./investor";
import type { TaxIncentiveTypeValue } from "./classification/constants";
import type { ProjectSubtype } from "./project";
import type { TagRef } from "./tag";

// Enums from backend (beneficiaries domain)
export type CompanyType =
  | "freelancer"
  | "limited_company"
  | "public_company"
  | "association"
  | "foundation"
  | "cooperative"
  | "other";
// Same catalog as investors — the backend mirrors one enum in both domains.
export const BENEFICIARY_CULTURAL_ACTIVITIES = [
  "film",
  "documentary",
  "series",
  "theatre",
  "dance",
  "music_festival",
  "performing_arts",
  "audiovisual_production",
] as const satisfies readonly CulturalActivity[];
export type { CulturalActivity };

export const ACTIVITIES_BY_INCENTIVE = {
  film_audiovisual: ["film", "documentary", "series", "audiovisual_production"],
  performing_arts_music: ["music_festival", "theatre", "dance", "performing_arts"],
} as const satisfies Record<TaxIncentiveTypeValue, readonly CulturalActivity[]>;

export function getActivitiesForIncentive(
  incentive: TaxIncentiveTypeValue | null | undefined,
): readonly CulturalActivity[] {
  return incentive ? ACTIVITIES_BY_INCENTIVE[incentive] : BENEFICIARY_CULTURAL_ACTIVITIES;
}

export function keepActivitiesForIncentive(
  activities: readonly CulturalActivity[],
  incentive: TaxIncentiveTypeValue | null | undefined,
): CulturalActivity[] {
  const allowed = getActivitiesForIncentive(incentive);
  return activities.filter((activity) => allowed.includes(activity));
}

// Matches backend BeneficiaryLegalRepresentativeResponse exactly
export type BeneficiarySortField =
  | "name"
  | "created_at"
  | "sector"
  | "total_projects_count"
  | "volume"
  | "available"
  | "quota_occupied";

export interface LegalRepresentative {
  uuid: string;
  first_name: string;
  last_name: string;
  full_name: string;
  dni: string;
  position: string | null;
  phone: string | null;
  email: string | null;
  is_primary: boolean;
  is_signer: boolean;
  created_at: string;
  updated_at: string;
}

// Matches backend BeneficiaryBankAccountResponse exactly
export interface BankAccount {
  uuid: string;
  bank_name: string | null;
  iban: string;
  swift: string | null;
  has_certificate: boolean;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

/** Partial PATCH: omitted fields stay, `null` clears an optional one. */
export interface BankAccountUpdateRequest {
  bank_name?: string | null;
  iban?: string;
  swift?: string | null;
  is_primary?: boolean;
}

export interface NewBankAccount {
  bank_name?: string;
  iban: string;
  swift?: string;
  is_primary: boolean;
}

// Type for creating bank account (used in API requests)
export interface CreateBankAccountRequest {
  bank_name: string;
  iban: string;
  swift: string;
  certificate?: File | null;
}

interface DeductionMetrics {
  total: string;
  reserved: string;
  completed: string;
  available: string;
  used: string;
  utilization_percentage: number;
  revised: string;
}

interface InvestmentMetrics {
  reserved: string; // ACTUAL - Sum of investments in RESERVED state
  completed: string; // ACTUAL - Sum of investments in FINANCING, FUNDED, or DECLARED states
  total_used: string; // ACTUAL - reserved + completed
  available_estimate: string | null; // ESTIMATED - Using default profitability
  capacity_estimate: string | null; // ESTIMATED - Total capacity using default profitability
}

export interface FiscalQuotaMoney {
  deduction: {
    estimated: string;
    reserved: string;
    completed: string;
    used: string;
    available: string;
    utilization: string;
  };
  investment: {
    reserved: string;
    completed: string;
    confirmed: string;
    committed: string;
  };
  estimate: {
    default_return_percentage: string;
    capacity: string;
    available: string;
  };
}

/**
 * Filter applied at the Overview tab to scope all widgets (KPIs, pipeline,
 * investments table) to a fiscal year + optional concrete quota.
 * `quotaUuid === null` means "yearly aggregate" (sum across every quota of
 * the year — relevant for film with N productions/year; equivalent to the
 * single quota for music).
 */
export interface BeneficiaryFiscalFilter {
  year: number;
  quotaUuid: string | null;
}

// Subsidy entry persisted on FiscalQuota
export function isSubsidyEntry(value: object): value is SubsidyEntry {
  const candidate = value as { title?: unknown; amount?: unknown };
  return typeof candidate.title === "string" && typeof candidate.amount === "number";
}

export interface SubsidyEntry {
  title: string;
  amount: number;
  // Backend types subsidies as free-form dicts; the index signature keeps the
  // request assignable until the schema gets a proper model.
  [key: string]: unknown;
}

/**
 * Cost breakdown JSONB payload on FiscalQuota.
 * Music uses: artistic, technical, promotional, other_non_deductible
 * Film uses: production, promotional, other_non_deductible
 */
export interface CostBreakdown {
  artistic?: number;
  technical?: number;
  promotional?: number;
  production?: number;
  other_non_deductible?: number;
}

/**
 * Form-state shape for the shared BudgetCostsSection (string values so the
 * inputs can be driven directly; subsidies use string amounts for the same
 * reason). The on-disk representation differs (numeric) — conversion happens at
 * submit time.
 */
export interface BudgetCostsData {
  total_budget: string;
  cost_breakdown: {
    artistic?: string;
    technical?: string;
    promotional?: string;
    production?: string;
    other_non_deductible?: string;
  };
  subsidies: Array<{ title: string; amount: string }>;
  total_eligible_cost: string;
  deduction_base: string;
  total_generated_deduction: string;
  own_contribution: string;
}

// Fiscal year aggregate response (GET /beneficiaries/{uuid}/fiscal-year-summary)
export interface FiscalYearAggregate {
  total_estimated_deduction: string;
  total_reserved: string;
  total_completed: string;
  total_used: string;
  total_available: string;
  utilization_percentage: number;

  reserved_investment: string;
  completed_investment: string;
  total_used_investment: string;
  available_estimate_investment: string | null;
  capacity_estimate_investment: string | null;

  total_budget: string | null;
  total_eligible_cost: string | null;
  total_deduction_base: string | null;
  total_generated_deduction: string | null;
  total_own_contribution: string | null;
}

export interface FiscalYearSummary {
  fiscal_year: number;
  quota_count: number;
  aggregate: FiscalYearAggregate;
  quotas: FiscalQuota[];
}

// Minimal project info attached to a FiscalQuota for display contexts.
// Mirrors backend LinkedProjectSummaryResponse.
export interface LinkedProjectSummary {
  uuid: string;
  title: string;
  subtype: ProjectSubtype | null;
  city: string | null;
  production_start_date: string | null;
  production_end_date: string | null;
}

// The five phases of a quota. Stored by the backend; never derived here.
export type FiscalQuotaState = "estimated" | "fundraising" | "auditing" | "consolidated" | "closed";

export const FISCAL_QUOTA_STATES: readonly FiscalQuotaState[] = [
  "estimated",
  "fundraising",
  "auditing",
  "consolidated",
  "closed",
];

export const isFiscalQuotaState = (value: unknown): value is FiscalQuotaState =>
  FISCAL_QUOTA_STATES.some((state) => state === value);

/** Mirror of `services/api/app/domain/beneficiaries/enums.py` `FiscalQuotaState.accepts_money_changes`. */
export const quotaAcceptsMoneyChanges = (state: FiscalQuotaState): boolean =>
  state !== "consolidated" && state !== "closed";

/** The three phases of the expense audit, served by the API (`audit_state`). */
export type AuditState = "no_documents" | "in_progress" | "consolidated";

// Matches backend FiscalQuotaResponse exactly
export interface FiscalQuota {
  uuid: string;
  fiscal_year: number;
  state: FiscalQuotaState;
  comments: string | null;
  default_profitability_percentage: string | null;
  deduction_rate: string;

  money: FiscalQuotaMoney;

  // Projects attached to this quota. Music: N for the year. Film: 0 or 1.
  linked_projects: LinkedProjectSummary[];

  // Financial fields (persisted on FiscalQuota)
  cost_breakdown: Record<string, string> | null;
  total_budget: string | null;
  total_eligible_cost: string | null;
  deduction_base: string | null;
  total_generated_deduction: string | null;
  own_contribution: string | null;
  subsidies: SubsidyEntry[] | null;

  // Consolidation snapshot (invoice_classification audit module). Null
  // for clients without the module or until the first sync is committed.
  consolidated_breakdown: Record<string, string> | null;
  consolidated_total: string | null;
  consolidated_at: string | null;
  consolidated_by_id: number | null;
  /** A4 — reopening keeps the snapshot above; it is recorded here instead. */
  consolidation_reopened_at: string | null;
  consolidation_reopened_by_id: number | null;
  /** A4 — where the expense audit stands, derived by the server in one place.
   *  Supersedes `auditStateOf()` of E8a, which cannot see a reopening: a
   *  reopened quota keeps its `consolidated_at` and is no longer consolidated. */
  audit_state: AuditState;

  created_at: string;
  updated_at: string;
}

// Matches backend ConsolidationBucketPreviewResponse.
// Both amounts are GROSS EXPENSE — apples-to-apples with quota.cost_breakdown.
export interface ConsolidationBucketPreview {
  bucket: string;
  estimated_amount: string;
  consolidated_amount: string;
  classification_count: number;
  classification_ids: number[];
}

// Matches backend ConsolidationBlockersResponse.
export interface ConsolidationBlockers {
  failed: number;
  requires_review: number;
  duplicates: number;
  total: number;
}

/**
 * Matches backend ConsolidationPreviewResponse.
 * Buckets are in gross expense; aggregate fields (eligible_cost,
 * deduction_base, generated_deduction) follow the wizard formula so the FE
 * can compare deduction-side metrics directly against the quota's
 * total_eligible_cost / deduction_base / total_generated_deduction.
 */
export interface ConsolidationPreview {
  fiscal_quota_uuid: string;
  incentive_type: TaxIncentiveTypeValue | null;
  buckets: ConsolidationBucketPreview[];
  consolidated_total: string; // sum of buckets INCLUDING other_non_deductible
  consolidated_eligible_cost: string; // sum of deductible buckets only
  consolidated_deduction_base: string; // deductible cost (base of the deduction): eligible − subsidies
  consolidated_generated_deduction: string; // deduction_base * rate
  pending_review_count: number;
  pending_review_amount: string; // gross expense pending
  pending_review_potential_deduction: string;
  /** A3 — what holds the consolidation back. A conditional never blocks. */
  blocking: ConsolidationBlockers;
  is_consolidated: boolean;
  last_consolidated_at: string | null;
  last_consolidated_by_id: number | null;
  new_since_last_sync: number | null;
}

// Matches backend ConsolidationCommitRequest
export interface ConsolidationCommitRequest {
  breakdown_overrides?: Record<string, string> | null;
  comment?: string | null;
}

// Simplified version used in list views - matches BeneficiaryCurrentYearQuotaSummary from backend
interface FiscalQuotaSummary {
  fiscal_year: number;

  // Nested metrics objects (same as FiscalQuota)
  deduction: DeductionMetrics;
  investment: InvestmentMetrics;
}

// Matches backend BeneficiaryPrimaryContactResponse exactly
export interface BeneficiaryPrimaryContact {
  uuid: string;
  full_name: string;
  position?: string | null;
}

// Matches backend BeneficiaryResponse exactly
export interface BeneficiaryDetail extends CreateBeneficiaryResponse {
  tags: TagRef[];
  // Optional fields for list views
  total_projects_count?: number;
  current_year_quota?: FiscalQuotaSummary | null;
  has_available_quota?: boolean;
  has_current_year_quota?: boolean;
  primary_contact?: BeneficiaryPrimaryContact | null;
}

export interface BeneficiaryDetailResponse {
  beneficiary: BeneficiaryDetail;
  legal_representatives: LegalRepresentative[];
  bank_accounts: BankAccount[];
  fiscal_quotas: FiscalQuota[];
  is_complete: boolean;
}

export interface BeneficiaryUser {
  id: string;
  ownerId: string; // Email of the user who owns this beneficiary user data
  name: string;
  email: string;
  phone: string;
  role: "beneficiary"; // Role value matches backend enum value
  status: "active" | "pending";
  avatar?: string;
}

export type PaginatedBeneficiaries = PaginatedList<BeneficiaryDetail, "beneficiaries">;

// Fiscal quota request interfaces - match backend schemas
export interface CreateFiscalQuotaRequest {
  fiscal_year: number;
  estimated_deduction: string;
  default_profitability_percentage: string;
  /**
   * Fiscal rate applied to the deduction base (e.g. "30.00" state film,
   * "20.00" state music). If omitted, the backend defaults based on the
   * beneficiary's incentive_type.
   */
  deduction_rate?: string | null;
  // Financial fields (optional)
  cost_breakdown?: Record<string, number> | null;
  total_budget?: string | null;
  total_eligible_cost?: string | null;
  deduction_base?: string | null;
  total_generated_deduction?: string | null;
  own_contribution?: string | null;
  subsidies?: SubsidyEntry[] | null;
}

export interface UpdateFiscalQuotaRequest {
  state?: FiscalQuotaState;
  estimated_deduction?: string;
  default_profitability_percentage?: string;
  deduction_rate?: string | null;
  // Financial fields (optional)
  cost_breakdown?: Record<string, number> | null;
  total_budget?: string | null;
  total_eligible_cost?: string | null;
  deduction_base?: string | null;
  total_generated_deduction?: string | null;
  own_contribution?: string | null;
  subsidies?: SubsidyEntry[] | null;
}

// Request type for updating a legal representative (PATCH)
export interface LegalRepUpdateRequest {
  first_name?: string;
  last_name?: string;
  dni?: string;
  position?: string | null;
  phone?: string | null;
  email?: string | null;
  is_primary?: boolean;
  is_signer?: boolean;
}

// Request type for updating beneficiary data
export interface BeneficiaryUpdateRequest {
  name?: string;
  tax_id?: string;
  company_type?: CompanyType;
  main_activities?: CulturalActivity[];
  constitution_date?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  business_purpose?: string;
  cnae_code?: string;
  last_year_revenue?: string;
  employee_count?: number | null;
  incentive_type?: "film_audiovisual" | "performing_arts_music" | null;
  signature_regime?: SignatureRegime;
  status?: BeneficiaryStatus;
}

// Response type for beneficiary creation/update operations
export interface CreateBeneficiaryResponse {
  uuid: string;
  name: string;
  tax_id: string;
  company_type: CompanyType;
  main_activities: CulturalActivity[];
  status: BeneficiaryStatus;
  constitution_date: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  notes: string | null;
  business_purpose: string | null;
  cnae_code: string | null;
  last_year_revenue: string | null;
  employee_count: number | null;
  incentive_type: TaxIncentiveTypeValue | null;
  signature_regime: SignatureRegime;
  created_at: string;
  updated_at: string;
}

export type BeneficiaryStatus = "active" | "inactive" | "archived";

export interface BeneficiarySort {
  key: BeneficiarySortField;
  direction: "asc" | "desc";
}

export interface BeneficiaryListFilters {
  search?: string;
  status?: BeneficiaryStatus[];
  sector?: CulturalActivity[];
  incentive_type?: TaxIncentiveTypeValue[];
  company_type?: CompanyType[];
  tag?: string[];
  has_current_year_quota?: boolean;
}

export interface BeneficiaryListParams extends BeneficiaryListFilters {
  sort?: BeneficiarySort[];
  sort_by?: BeneficiarySortField;
  sort_direction?: "asc" | "desc" | null;
  limit?: number;
  offset?: number;
}

export const BENEFICIARY_AGGREGATE_FUNCTIONS = {
  total_projects_count: ["sum", "avg", "min", "max"],
  volume: ["sum", "avg", "min", "max"],
  quota_occupied: ["avg", "min", "max"],
  primary_contact: ["filled", "empty", "unique"],
  city: ["filled", "empty", "unique"],
  email: ["filled", "empty", "unique"],
  sector: ["distribution"],
  tag: ["distribution"],
  status: ["active", "inactive", "pct_active"],
} as const;

export type BeneficiaryAggregateField = keyof typeof BENEFICIARY_AGGREGATE_FUNCTIONS;

type AggregatePair<F extends BeneficiaryAggregateField> = F extends BeneficiaryAggregateField
  ? `${F}:${(typeof BENEFICIARY_AGGREGATE_FUNCTIONS)[F][number]}`
  : never;

export type BeneficiaryAggregateKey = AggregatePair<BeneficiaryAggregateField>;

export type BeneficiaryAggregateValue = number | string | Record<string, number> | null;

export type BeneficiaryAggregates = Partial<
  Record<BeneficiaryAggregateKey, BeneficiaryAggregateValue>
>;

export interface BeneficiaryAggregatesParams extends BeneficiaryListFilters {
  aggregates: BeneficiaryAggregateKey[];
}

export interface BeneficiaryMetricsParams {
  search?: string;
  status?: "active" | "inactive";
  sector?: CulturalActivity;
}

export interface BeneficiaryExportParams extends BeneficiaryListFilters {
  sort?: BeneficiarySort[];
}

// Matches backend BeneficiaryMetricsResponse exactly
interface BeneficiaryStatusBreakdown {
  active: number;
  inactive: number;
}

export interface BeneficiaryMetrics {
  total_count: number;
  by_status: BeneficiaryStatusBreakdown;
  total_capacity: string; // Decimal as string from API
  available: string; // Decimal as string from API
  reserved: string; // Decimal as string from API
  completed: string; // Decimal as string from API
}

export interface BeneficiaryWizardRepresentative {
  fullName: string;
  dni: string;
  position: string;
  phone: string;
  email: string;
}
